import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import languagesData from '../locales/languages.json';
import { useCart } from '../hooks/useCart';
import { useAuth } from '../hooks/useAuth';

interface LayoutProps {
  children: React.ReactNode;
  lang: string;
  setLang: (l: string) => void;
}

const CREAM = '#F4F1E1';
const RED = '#8B1A1A';
const BLACK = '#0a0c0a';
const LANGS = ['EN', 'DE', 'FR', 'ES', 'GA', 'PL', 'RO', 'PT', 'HI', 'ZH'];
const FLAGS: Record<string, string> = {
  EN: 'https://flagcdn.com/w20/gb.png',
  DE: 'https://flagcdn.com/w20/de.png',
  FR: 'https://flagcdn.com/w20/fr.png',
  ES: 'https://flagcdn.com/w20/es.png',
  GA: 'https://flagcdn.com/w20/ie.png',
  PL: 'https://flagcdn.com/w20/pl.png',
  RO: 'https://flagcdn.com/w20/ro.png',
  PT: 'https://flagcdn.com/w20/pt.png',
  HI: 'https://flagcdn.com/w20/in.png',
  ZH: 'https://flagcdn.com/w20/cn.png',
};

const NAV_ITEMS = [
  { label: 'Shop', path: '/shop', sub: [
    { label: 'Ukuleles', icon: '🪕', path: '/shop/ukuleles' },
    { label: 'Guitars', icon: '🎸', path: '/shop/guitars', sub: [
      { label: 'Acoustic Guitars', icon: '🪕', path: '/shop/acoustic-guitars' },
      { label: 'Electric Guitars', icon: '🎸', path: '/shop/electric-guitars' },
      { label: 'Classical Guitars', icon: '🪕', path: '/shop/classical-guitars' },
      { label: 'Bass Guitars', icon: '🪕', path: '/shop/bass-guitars' },
      { label: 'CigarBox Guitars', icon: '🪕', path: '/shop/cigarbox-guitars' },
    ]},
    { label: 'Traditional & Folk', icon: '🪗', path: '/shop/traditional', sub: [
      { label: 'Mandolin', icon: '🪕', path: '/shop/mandolin' },
      { label: 'Banjo', icon: '🪕', path: '/shop/banjo' },
      { label: 'Bouzouki', icon: '🪕', path: '/shop/bouzouki' },
      { label: 'Concertina', icon: '🪗', path: '/shop/concertina' },
      { label: 'Bodhran', icon: '🥁', path: '/shop/bodhran' },
      { label: 'Tin Whistles', icon: '🪈', path: '/shop/tin-whistles' },
      { label: 'Harps', icon: '🎻', path: '/shop/harps' },
      { label: 'Violins', icon: '🎻', path: '/shop/violins' },
      { label: 'Accordians', icon: '🪗', path: '/shop/accordians' },
    ]},
    { label: 'Keyboards & Pianos', icon: '🎹', path: '/shop/keyboards' },
    { label: 'Drums', icon: '🥁', path: '/shop/drums' },
    { label: 'Wind Instruments', icon: '🎷', path: '/shop/wind', sub: [
      { label: 'Flutes', icon: '🎵', path: '/shop/flutes' },
      { label: 'Alto Sax', icon: '🎷', path: '/shop/alto-sax' },
      { label: 'Clarinet', icon: '🎷', path: '/shop/clarinet' },
      { label: 'Recorders', icon: '🎵', path: '/shop/recorders' },
      { label: 'Reeds', icon: '🎵', path: '/shop/reeds' },
    ]},
    { label: 'Strings', icon: '🎻', path: '/shop/strings', sub: [
      { label: 'Acoustic Strings', icon: '🎻', path: '/shop/acoustic-strings' },
      { label: 'Electric Strings', icon: '🎻', path: '/shop/electric-strings' },
      { label: 'Classical Strings', icon: '🎻', path: '/shop/classical-strings' },
      { label: 'Bass Strings', icon: '🎻', path: '/shop/bass-strings' },
      { label: 'Ukulele Strings', icon: '🎻', path: '/shop/ukulele-strings' },
      { label: 'Mandolin Strings', icon: '🎻', path: '/shop/mandolin-strings' },
      { label: 'Banjo Strings', icon: '🎻', path: '/shop/banjo-strings' },
      { label: 'Bouzouki Strings', icon: '🎻', path: '/shop/bouzouki-strings' },
    ]},
    { label: 'Studio & Sound', icon: '🎙', path: '/shop/studio-sound', sub: [
      { label: 'Studio', icon: '🎙', path: '/shop/studio' },
      { label: 'Microphones', icon: '🎤', path: '/shop/microphones' },
      { label: 'Speakers', icon: '🔊', path: '/shop/speakers' },
    ]},
    { label: 'Music Books', icon: '📚', path: '/shop/music-books' },
    { label: 'Accessories', icon: '🎧', path: '/shop/accessories' },
    { label: 'Pre-Loved Gear', icon: '🔄', path: '/shop/pre-loved' },
    { label: 'Shop Offers', icon: '📣', path: '/shop/offers' },
    { label: 'Other', icon: '🎚️', path: '/shop/other' },
  ]},
  { label: 'Rental', path: '/rental', sub: [] },
  { label: 'Blog', path: '/blog', sub: [] },
  { label: 'Our Story', path: '/our-story', sub: [] },
  { label: 'Contact', path: '/contact', sub: [] },
];

const OPENING_HOURS: Record<number, { open: number; close: number } | null> = {
  0: null,
  1: { open: 10 * 60, close: 17 * 60 + 30 },
  2: { open: 10 * 60, close: 17 * 60 + 30 },
  3: { open: 10 * 60, close: 17 * 60 + 30 },
  4: { open: 10 * 60, close: 18 * 60 },
  5: { open: 10 * 60, close: 17 * 60 + 30 },
  6: { open: 10 * 60, close: 18 * 60 },
};

const MAPS_URL = 'https://www.google.com/maps/dir/?api=1&destination=52.6517554,-7.2513701';

const ShimmerStyle: React.FC = () => (
  <style>{`
    @keyframes shimmer {
      0% { background-position: -200% center; }
      100% { background-position: 200% center; }
    }
    .essaness-logo-shimmer {
      filter: drop-shadow(0 2px 8px rgba(139,26,26,0.5));
      animation: none;
      transition: filter 0.3s;
    }
    .essaness-logo-shimmer:hover {
      filter: drop-shadow(0 2px 16px rgba(200,50,50,0.8)) brightness(1.2);
    }
    .shimmer-wrap {
      position: relative;
      display: inline-flex;
      overflow: hidden;
    }
    .shimmer-wrap::after {
      content: '';
      position: absolute;
      top: 0; left: 0; right: 0; bottom: 0;
      background: linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.25) 50%, transparent 70%);
      background-size: 200% 100%;
      animation: shimmer 3.5s ease-in-out infinite;
      pointer-events: none;
      border-radius: 4px;
    }
  `}</style>
);

const EssanessLogo: React.FC<{ size?: number }> = ({ size = 48 }) => (
  <div className="shimmer-wrap" style={{ flexShrink: 0 }}>
    <img
      src="/essaness-logo.png"
      alt="Essaness Music Logo"
      className="essaness-logo-shimmer"
      style={{ height: size, width: 'auto', display: 'block' }}
    />
  </div>
);

const LightningLogo: React.FC<{ w?: number; h?: number; dimS?: string; dimN?: string; hoverS?: string; hoverN?: string }> = ({
  w = 36, h = 36, dimS = '#aaaaaa', dimN = '#666660', hoverS = CREAM, hoverN = RED
}) => {
  const [hovered, setHovered] = useState(false);
  return (
    <svg width={w} height={h} viewBox="0 0 680 200"
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ cursor: 'pointer', flexShrink: 0 }}>
      <g transform="translate(340,100) skewX(-12)">
        <path d="M -100 -58 L -86 -58 L -94 -10 L -72 -10 L -100 62 L -90 14 L -112 14 Z"
          style={{ fill: hovered ? hoverS : dimS, transition: 'fill 0.8s ease' }} />
        <path d="M -42 -58 L -33 -58 L -33 18 L -9 -58 L 0 -58 L 0 62 L -9 62 L -9 -14 L -33 62 L -42 62 Z"
          style={{ fill: hovered ? hoverN : dimN, transition: 'fill 0.8s ease' }} />
        <path d="M 58 -58 L 72 -58 L 64 -10 L 86 -10 L 58 62 L 68 14 L 46 14 Z"
          style={{ fill: hovered ? hoverS : dimS, transition: 'fill 0.8s ease' }} />
      </g>
    </svg>
  );
};

const LanguageSwitcher: React.FC<{ current: string; onChange: (l: string) => void }> = ({ current, onChange }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <button onClick={() => setOpen(!open)}
        style={{ background: 'rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.2)', color: BLACK, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', padding: '5px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
        <img src={FLAGS[current]} alt={current} style={{ width: 20, height: 14, objectFit: 'cover' }} />
        {current} ▾
      </button>
      {open && (
        <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, background: BLACK, border: '1px solid rgba(255,255,255,0.12)', zIndex: 300, minWidth: 110 }}>
          {LANGS.map(l => (
            <button key={l} onClick={() => { onChange(l); setOpen(false); }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '8px 14px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', background: 'none', border: 'none', color: l === current ? RED : 'rgba(255,255,255,0.55)', cursor: 'pointer' }}>
              <img src={FLAGS[l]} alt={l} style={{ width: 20, height: 14, objectFit: 'cover' }} /> {l}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const TopBar: React.FC = () => {
  const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Dublin' }));
  const day = now.getDay();
  const mins = now.getHours() * 60 + now.getMinutes();
  const hours = OPENING_HOURS[day];
  const isOpen = hours !== null && mins >= hours.open && mins < hours.close;
  const statusColor = isOpen ? '#2d9e2d' : '#8B1A1A';
  const glowColor = isOpen ? 'rgba(45,158,45,0.6)' : 'rgba(139,26,26,0.6)';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 20, flex: 1, justifyContent: 'center', flexWrap: 'nowrap', overflow: 'hidden', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: statusColor, boxShadow: `0 0 6px 2px ${glowColor}` }} />
        <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: statusColor }}>
          {isOpen ? 'Open Now' : 'Closed'}
        </span>
      </div>
      <a href="tel:+353567765693" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(0,0,0,0.5)', textDecoration: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
        onMouseEnter={e => (e.currentTarget.style.color = RED)}
        onMouseLeave={e => (e.currentTarget.style.color = 'rgba(0,0,0,0.5)')}>
        +353 (0)56 776 5693
      </a>
      <a href="tel:+353567765693"
        style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'white', textDecoration: 'none', fontWeight: 700, whiteSpace: 'nowrap', background: RED, padding: '4px 10px' }}
        onMouseEnter={e => (e.currentTarget.style.background = BLACK)}
        onMouseLeave={e => (e.currentTarget.style.background = RED)}>
        Call Now
      </a>
      <a href={MAPS_URL} target="_blank" rel="noreferrer"
        style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'white', textDecoration: 'none', fontWeight: 700, padding: '4px 10px', background: '#4285F4', border: 'none', borderRadius: 2, boxShadow: '0 1px 4px rgba(66,133,244,0.5)', transition: 'all 0.2s', whiteSpace: 'nowrap' }}
        onMouseEnter={e => { e.currentTarget.style.background = '#1a73e8'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(66,133,244,0.7)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = '#4285F4'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(66,133,244,0.5)'; }}>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="white" stroke="none">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
        </svg>
        Get Directions
      </a>
    </div>
  );
};


const SubMenuItem: React.FC<{ sub: any; navigate: any; setActive: any }> = ({ sub, navigate, setActive }) => {
  const [hovered, setHovered] = useState(false);
  const hasSub = sub.sub && sub.sub.length > 0;
  return (
    <div style={{ position: 'relative' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={(e) => {
        const related = e.relatedTarget as Node;
        if (!e.currentTarget.contains(related)) setHovered(false);
      }}>
      <a onClick={() => { navigate(sub.path); setActive(null); }}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '11px 18px', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: hovered ? 'white' : 'rgba(255,255,255,0.80)', background: hovered ? 'rgba(255,255,255,0.08)' : 'none', textDecoration: 'none', borderBottom: '1px solid rgba(255,255,255,0.06)', cursor: 'pointer', fontWeight: 500 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {sub.icon && <span style={{fontSize:16}}>{sub.icon}</span>}
          {sub.label}
        </span>
        {hasSub && <span style={{ opacity: 0.5, fontSize: 10 }}>▶</span>}
      </a>
      {hasSub && hovered && (
        <div style={{ position: 'absolute', left: '100%', top: '-1px', background: '#0d0f0d', border: '1px solid rgba(255,255,255,0.15)', zIndex: 500, minWidth: 220, boxShadow: '8px 8px 32px rgba(0,0,0,0.8)',
          // Same cap one level deeper. This panel opens level with its parent item, so
          // how much room it has depends on how far down the list that item sits - the
          // cap bounds the worst case rather than measuring it, which is enough for a
          // tree this size.
          maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', overscrollBehavior: 'contain',
          scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.25) transparent' }}>
          {sub.sub.map((sub2: any) => (
            <a key={sub2.label} onClick={() => { navigate(sub2.path); setActive(null); }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 18px', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(255,255,255,0.80)', textDecoration: 'none', borderBottom: '1px solid rgba(255,255,255,0.06)', cursor: 'pointer', fontWeight: 500 }}
              onMouseEnter={e => { e.currentTarget.style.color = 'white'; e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.80)'; e.currentTarget.style.background = 'none'; }}>
              {sub2.icon && <span style={{fontSize:16}}>{sub2.icon}</span>}
              {sub2.label}
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

const DesktopNav: React.FC<{ lang: string }> = ({ lang }) => {
  const [active, setActive] = useState<string | null>(null);
  const navigate = useNavigate();
  const languages = languagesData as Record<string, any>;
  const t = languages[lang] || languages['EN'];

  return (
    <nav style={{ display: 'flex', alignItems: 'center' }}>
      {NAV_ITEMS.map((item, i) => (
        <div key={item.label} style={{ position: 'relative' }}
          onMouseEnter={() => setActive(item.label)}
          onMouseLeave={(e) => {
            // Only close if not moving to a child element
            const related = e.relatedTarget as Node;
            if (!e.currentTarget.contains(related)) setActive(null);
          }}>
          <a onClick={() => navigate(item.path)}
            style={{ display: 'block', padding: '0 16px', height: 48, lineHeight: '48px', fontSize: 14, fontWeight: 900, fontStyle: 'italic', fontFamily: 'Georgia, serif', letterSpacing: '0.02em', color: item.sub.length > 0 ? (active === item.label ? 'white' : 'rgba(255,255,255,0.90)') : (active === item.label ? 'white' : 'rgba(255,255,255,0.80)'), textDecoration: 'none', whiteSpace: 'nowrap', transition: 'all 0.2s', cursor: 'pointer', borderBottom: active === item.label ? '2px solid #8B1A1A' : '2px solid transparent' } as any}>
            {t.nav[i] || item.label}{item.sub.length > 0 ? ' ▾' : ''}
          </a>
          {item.sub.length > 0 && active === item.label && (
            <div style={{ position: 'absolute', top: '100%', left: 0, background: '#161816', border: '1px solid rgba(255,255,255,0.12)', zIndex: 200, minWidth: 240, boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
              // NO overflow on this panel. Its children are the level-two flyouts at
              // left:100%, which live outside this box. An overflowY here forces
              // overflow-x to auto as well, so the flyouts are clipped away and a
              // horizontal scrollbar appears in their place - the sub-categories simply
              // vanish. Reported by the shop owner the same afternoon, 2026-10-10.
              // Capping a panel that owns flyouts needs those flyouts rendered in a
              // portal, or a multi-column panel short enough never to scroll. That is
              // Phase 3 nav work, not a style tweak.
            }}>
              {item.sub.map(sub => (
                <SubMenuItem key={sub.label} sub={sub} navigate={navigate} setActive={setActive} />
              ))}
            </div>
          )}
        </div>
      ))}
    </nav>
  );
};

const MobileMenu: React.FC<{ open: boolean; lang: string; onClose: () => void }> = ({ open, lang, onClose }) => {
  const navigate = useNavigate();
  const languages = languagesData as Record<string, any>;
  const t = languages[lang] || languages['EN'];
  const [expanded, setExpanded] = useState<string | null>(null);
  const [expanded2, setExpanded2] = useState<string | null>(null);
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 400,
      pointerEvents: open ? 'auto' : 'none',
    }}>
      <div onClick={onClose} style={{
        position: 'absolute', inset: 0,
        background: 'rgba(0,0,0,0.7)',
        opacity: open ? 1 : 0,
        transition: 'opacity 0.25s ease',
      }} />
      <div style={{
        position: 'absolute', top: 0, right: 0, bottom: 0,
        width: '85%', maxWidth: 360,
        background: BLACK,
        overflowY: 'auto',
        transform: open ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.28s cubic-bezier(0.4,0,0.2,1)',
        boxShadow: '-4px 0 32px rgba(0,0,0,0.6)',
      }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <EssanessLogo size={36} />
          <span style={{ color: RED, fontSize: 22, fontWeight: 900, fontStyle: 'italic' }}>ESSANESS MUSIC</span>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: 22, cursor: 'pointer', minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
      </div>
      {NAV_ITEMS.map((item, i) => (
        <div key={item.label} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <button onClick={() => { navigate(item.path); onClose(); }}
              style={{ flex: 1, textAlign: 'left', padding: '18px 24px', background: 'none', border: 'none', color: 'white', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', cursor: 'pointer' }}>
              {t.nav[i] || item.label}
            </button>
            {item.sub.length > 0 && (
              <button onClick={() => setExpanded(expanded === item.label ? null : item.label)}
                style={{ padding: '18px 20px', background: 'none', border: 'none', borderLeft: '1px solid rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.4)', fontSize: 13, cursor: 'pointer', flexShrink: 0 }}>
                {expanded === item.label ? '▴' : '▾'}
              </button>
            )}
          </div>
          {expanded === item.label && item.sub.map(sub => (
            <div key={sub.label} style={{ background: '#141614' }}>
              <a onClick={() => { if ((sub as any).sub?.length > 0) { setExpanded2(expanded2 === sub.label ? null : sub.label); } else { navigate(sub.path); onClose(); } }}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '11px 24px 11px 32px', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.75)', textDecoration: 'none', borderTop: '1px solid rgba(255,255,255,0.06)', cursor: 'pointer', fontWeight: 500 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {(sub as any).icon && <span style={{fontSize:15}}>{(sub as any).icon}</span>}
                  {sub.label}
                </span>
                {(sub as any).sub?.length > 0 && <span style={{ opacity: 0.4, fontSize: 10 }}>{expanded2 === sub.label ? '▴' : '▾'}</span>}
              </a>
              {expanded2 === sub.label && (sub as any).sub?.map((sub2: any) => (
                <a key={sub2.label} onClick={() => { navigate(sub2.path); onClose(); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 24px 9px 48px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.55)', textDecoration: 'none', borderTop: '1px solid rgba(255,255,255,0.04)', cursor: 'pointer', fontWeight: 500, background: '#1a1c1a' }}>
                  {sub2.icon && <span style={{fontSize:13}}>{sub2.icon}</span>}
                  {sub2.label}
                </a>
              ))}
            </div>
          ))}
        </div>
      ))}
      <div style={{ padding: 24 }}>
        <button onClick={() => { navigate('/shop'); onClose(); }} style={{ width: '100%', background: RED, color: 'white', border: 'none', padding: '14px', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.12em', cursor: 'pointer' }}>
          Shop Online
        </button>
      </div>
      </div>
    </div>
  );
};

const Footer: React.FC = () => {
  const navigate = useNavigate();
  return (
    <footer style={{ backgroundColor: BLACK, padding: '12px 40px 8px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div>
            <p style={{ color: RED, fontWeight: 900, fontStyle: 'italic', fontSize: 20, margin: '0 0 2px' }}>ESSANESS MUSIC</p>
            <p style={{ color: 'rgba(244,241,225,0.4)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0 }}>Kilkenny City | Est. 1986</p>
          </div>
          <EssanessLogo size={112} />
        </div>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
          {[['Privacy Policy', '/privacy'], ['Cookies', '/cookies'], ['Terms', '/terms'], ['Shipping & Returns', '/shipping'], ['Contact', '/contact']].map(([label, path]) => (
            <a key={label} onClick={() => navigate(path)}
              style={{ color: 'rgba(244,241,225,0.55)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', textDecoration: 'none', cursor: 'pointer', transition: 'color 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.color = CREAM}
              onMouseLeave={e => e.currentTarget.style.color = 'rgba(244,241,225,0.55)'}>
              {label}
            </a>
          ))}
        </div>
        <div>
          <p style={{ color: 'rgba(244,241,225,0.35)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 4px' }}>Powered by</p>
          <a href="https://dbos.world" target="_blank" rel="noreferrer" style={{ color: RED, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', textDecoration: 'none' }}>DBOS.world</a>
          <p style={{ color: 'rgba(244,241,225,0.25)', fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.08em', margin: '4px 0 0' }}>Ireland · GIDO_IE_0001</p>
        </div>
      </div>
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: 8, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 4 }}>
        <p style={{ color: 'rgba(244,241,225,0.3)', fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0 }}>© 2026 DBOS Framework · dbos.world · All rights reserved</p>
        <p style={{ color: 'rgba(244,241,225,0.2)', fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>DBOS World Ireland · GIDO_IE_0001</p>
      </div>
    </footer>
  );
};

const CookieBanner: React.FC = () => {
  const [visible, setVisible] = useState(() => !localStorage.getItem('cookie_consent'));
  if (!visible) return null;
  return (
    <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: '#0a0c0a', borderTop: '1px solid rgba(255,255,255,0.1)', padding: '16px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 1000, gap: 20, flexWrap: 'wrap' }}>
      <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', margin: 0, flex: 1, minWidth: 280 }}>
        We use essential cookies for shop functionality and Stripe payments. Analytics cookies (Google Analytics, Microsoft Clarity) are only used with your consent.{' '}
        <a href="/cookies" style={{ color: '#8B1A1A', textDecoration: 'none' }}>Cookie Policy</a> ·{' '}
        <a href="/privacy" style={{ color: '#8B1A1A', textDecoration: 'none' }}>Privacy Policy</a>
      </p>
      <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
        <button onClick={() => { localStorage.setItem('cookie_consent', 'essential'); setVisible(false); }}
          style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.15)', padding: '8px 16px', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', cursor: 'pointer' }}>
          Essential Only
        </button>
        <button onClick={() => { localStorage.setItem('cookie_consent', 'all'); setVisible(false); }}
          style={{ background: '#8B1A1A', color: 'white', border: 'none', padding: '8px 16px', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', cursor: 'pointer' }}>
          Accept All
        </button>
      </div>
    </div>
  );
};

export const Layout: React.FC<LayoutProps> = ({ children, lang, setLang }) => {
  const { cart, loadCart } = useCart();
  const { signedIn } = useAuth();
  const location = useLocation();
  useEffect(() => { loadCart(); }, [loadCart, location.pathname]);
  useEffect(() => {
    const onCartChanged = () => loadCart();
    window.addEventListener('cart:changed', onCartChanged);
    return () => window.removeEventListener('cart:changed', onCartChanged);
  }, [loadCart]);
  const hasItems = (cart?.item_count ?? 0) > 0;
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div style={{ minHeight: '100vh', backgroundColor: CREAM, fontFamily: 'system-ui,-apple-system,sans-serif', display: 'flex', flexDirection: 'column' }}>
      <style>{`
        @media(max-width:860px){#nav-row{display:none!important}}
        @media(min-width:861px){#burger{display:none!important}}
        @media(max-width:860px){#top-cream-bar{display:none!important}}
        @media(max-width:860px){#logo-row{padding:0 16px!important;height:56px!important}}
        @media(max-width:860px){
          #logo-row a img{height:34px!important}
          #shop-btn,#cart-btn{padding:6px 10px!important;font-size:10px!important}
          #acct-btn{padding:5px 8px!important}
          #logo-row > div{gap:6px!important}
        }
        @media(max-width:860px){#logo-text{display:none!important}}
        @media(max-width:600px){
          #main-layout{flex-direction:column!important;padding:0 16px 48px!important}
          #sidebar{width:100%!important;border-right:none!important;border-bottom:1px solid rgba(0,0,0,0.08)!important;padding-right:0!important;padding-bottom:20px!important;margin-bottom:20px!important}
          #content{padding-left:0!important}
        }
      `}</style>

      <header style={{ position: 'sticky', top: 0, zIndex: 200 }}>
        <div id="top-cream-bar" style={{ backgroundColor: CREAM, borderBottom: '1px solid rgba(0,0,0,0.08)', padding: '6px 32px', display: 'grid', gridTemplateColumns: 'auto 1fr auto', alignItems: 'center', gap: 16 }}>
          <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'rgba(0,0,0,0.35)', whiteSpace: 'nowrap' }}>Essaness Music · Kilkenny</span>
          <TopBar />
          <LanguageSwitcher current={lang} onChange={setLang} />
        </div>
        <div style={{ background: 'linear-gradient(180deg, #1a1c1a 0%, #0a0c0a 40%, #060806 100%)', borderBottom: '1px solid rgba(255,255,255,0.04)', boxShadow: '0 4px 24px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.06)' }}>
          <div id="logo-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 32px', height: 68, borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
            <a onClick={() => { navigate('/'); window.scrollTo(0,0); }} style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none', cursor: 'pointer' }}>
              <EssanessLogo size={48} />
              <span id="logo-text" style={{ fontSize: 'clamp(18px, 4vw, 38px)', fontWeight: 900, fontStyle: 'italic', letterSpacing: '-0.5px', background: 'linear-gradient(180deg, #d44 0%, #8B1A1A 50%, #c33 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.8))' }}>ESSANESS MUSIC</span>
            </a>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button id="shop-btn" onClick={() => navigate('/shop')} style={{ background: RED, color: 'white', border: 'none', padding: '9px 20px', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', cursor: 'pointer' }}>
                Shop
              </button>
              <button id="cart-btn" onClick={() => navigate("/cart")} style={{
                background: hasItems ? 'rgba(74,222,128,0.12)' : 'none',
                color: 'white',
                border: hasItems ? '1px solid #4ade80' : '1px solid rgba(255,255,255,0.3)',
                boxShadow: hasItems ? '0 0 0 1px rgba(74,222,128,0.3), 0 0 12px rgba(74,222,128,0.5)' : 'none',
                animation: hasItems ? 'cartShimmer 2s ease-in-out infinite' : 'none',
                padding: '9px 20px', fontWeight: 700, fontSize: 11, textTransform: 'uppercase',
                letterSpacing: '0.1em', cursor: 'pointer', transition: 'all 0.3s ease',
              }}>
                Cart{hasItems ? ` (${cart!.item_count})` : ''}
              </button>
              <button id="acct-btn" onClick={() => navigate('/account')}
                aria-label={signedIn ? 'My account' : 'Sign in or create an account'}
                title={signedIn ? 'My account' : 'Sign in'}
                style={{ background: 'none', border: signedIn ? `1px solid ${RED}` : '1px solid rgba(255,255,255,0.3)', padding: '7px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.3s ease' }}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={signedIn ? RED : 'rgba(255,255,255,0.85)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </button>
              <button id="burger" onClick={() => setMobileOpen(true)}
                aria-label="Open menu"
                style={{ minWidth: 44, minHeight: 44, background: 'none', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '4px 2px' }}>
                <span style={{ display: 'block', width: 24, height: 2, background: 'rgba(255,255,255,0.7)' }} />
                <span style={{ display: 'block', width: 24, height: 2, background: 'rgba(255,255,255,0.7)' }} />
                <span style={{ display: 'block', width: 24, height: 2, background: 'rgba(255,255,255,0.7)' }} />
              </button>
            </div>
          </div>
          <div id="nav-row" style={{ padding: '0 32px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
            <DesktopNav lang={lang} />
          </div>
        </div>
      </header>

      <MobileMenu open={mobileOpen} lang={lang} onClose={() => setMobileOpen(false)} />
      <CookieBanner />

      <main style={{ flex: 1 }}>
        {children}
      </main>

      <Footer />
    </div>
  );
};

export { CREAM, RED, BLACK, LightningLogo };
